import React from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { SPACING } from '../../theme';
import { useEscapeToClose } from '../../utils/escapeStack';
import { usePostsStore } from '../../store/postsStore';
import { normalizePostCategory } from '../../types';
import { PanelHeader } from '../layout/PanelHeader';
import { PostDetailBody } from './PostDetailBody';

interface PostDetailPanelProps {
  postId: string;
  onClose: () => void;
}

/** nativeID for the title, wired to the DockedPanel's ariaLabelledBy —
 *  panels are regions, never dialogs (CLAUDE.md's "Panels, not dialogs"). */
export const POST_PANEL_HEADING_ID = 'post-panel-heading';

/**
 * Right-docked observation panel (desktop map, panel-system.md Phase 2): a
 * pinned title bar and close button around a scrolling PostDetailBody,
 * opened from a map marker click (app/(tabs)/index.tsx's `dockedPostId`)
 * instead of routing to /post/[id]. Comments render inline (PostDetailBody's
 * own isDesktopShell branch, i.e. always here) — this panel never mounts
 * CommentsSheet.
 *
 * Mounted by a `DockedPanel` with `scroll={false}` — the header below stays
 * fixed and only the body scrolls (panel-system.md review, note 4), with
 * SPACING.lg of bottom padding so the comment composer at the end isn't
 * flush with the panel's edge (note 6).
 */
export function PostDetailPanel({ postId, onClose }: PostDetailPanelProps) {
  useEscapeToClose(true, onClose);
  const post = usePostsStore((s) => s.postsById[postId] ?? null);
  const title = !post
    ? 'Observation'
    : normalizePostCategory(post.category) === 'news'
      ? post.title || 'News post'
      : post.address || 'Observation';

  return (
    <View style={styles.container}>
      <PanelHeader
        title={title}
        onClose={onClose}
        headingId={POST_PANEL_HEADING_ID}
        testID="post-panel-close"
      />
      <ScrollView
        style={styles.scrollBody}
        contentContainerStyle={styles.scrollBodyContent}
        showsVerticalScrollIndicator={false}>
        <PostDetailBody postId={postId} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  scrollBody: {
    flex: 1,
  },
  scrollBodyContent: {
    paddingBottom: SPACING.lg,
  },
});
